import { useEffect, useState } from "react";
import { X, Trophy } from "lucide-react";

// Contest voting deadline (UTC). Banner hides automatically after this.
// Emergent Builder Fest — closes Sept 1, 2026. Padded to Sept 10 so any
// clock skew / grace period on Emergent's side doesn't hide the banner
// early. Delete this component or bump the date for the next contest.
const VOTE_END_ISO = "2026-09-10T23:59:59Z";
const VOTE_URL =
  "https://app.emergent.sh/showcase/builderfest-kevin/5a62c035-d01c-4302-8c74-1f194086f7f7?ref=rock130069&utm_source=share";
const DISMISS_KEY = "drr_builder_fest_dismissed_v1";

// Big, unmissable, pulsing red bar that sits at the top of The Pit and asks
// every listener to vote for DRR in the Emergent Builder Fest. Because the
// most engaged listeners live inside the chat, this is our best-converting
// surface for votes. Dismissable per-visitor + auto-vanishes after Sept 1.
export default function ChatVoteBanner() {
  const [dismissed, setDismissed] = useState(true);
  const [remaining, setRemaining] = useState("");

  // Read the dismissed flag on mount so SSR / hydration doesn't flash.
  useEffect(() => {
    try {
      const flag = localStorage.getItem(DISMISS_KEY);
      setDismissed(flag === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  // Recompute the "X days left" label every minute. Cheap, only runs while
  // the banner is mounted (which is only when the user is inside The Pit).
  useEffect(() => {
    const compute = () => {
      const end = new Date(VOTE_END_ISO).getTime();
      const now = Date.now();
      const diff = end - now;
      if (diff <= 0) {
        setRemaining("");
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      if (days >= 1) {
        setRemaining(`${days}d ${hours}h left`);
      } else {
        const mins = Math.floor((diff / (1000 * 60)) % 60);
        setRemaining(`${hours}h ${mins}m left`);
      }
    };
    compute();
    const t = setInterval(compute, 60_000);
    return () => clearInterval(t);
  }, []);

  const contestOver = Date.now() >= new Date(VOTE_END_ISO).getTime();
  if (dismissed || contestOver) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore quota / private mode */
    }
    setDismissed(true);
  };

  const handleClick = () => {
    // Fire-and-forget: log a click so we know the banner is converting.
    // Don't await — never block the outbound tab open.
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
    window.open(VOTE_URL, "_blank", "noopener,noreferrer");
    setDismissed(true);
  };

  return (
    <div
      className="relative overflow-hidden border-y-2 border-red-600 bg-gradient-to-r from-red-700 via-red-600 to-red-700 animate-pulse-slow"
      data-testid="chat-vote-banner"
    >
      {/* Diagonal warning stripes overlay for a "this is important" feel. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(0,0,0,0.5) 0 8px, transparent 8px 16px)",
        }}
      />
      <button
        type="button"
        onClick={handleClick}
        className="relative w-full flex items-center justify-between gap-3 px-3 md:px-4 py-3 text-left"
        data-testid="chat-vote-banner-click"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Trophy
            className="w-5 h-5 md:w-6 md:h-6 text-yellow-300 shrink-0 drop-shadow"
            strokeWidth={2.5}
          />
          <div className="min-w-0">
            <div className="font-display tracking-wide text-white text-sm md:text-base leading-tight">
              🤘 VOTE FOR DRR IN{" "}
              <span className="text-yellow-300">BUILDER FEST</span>
            </div>
            <div className="text-[10px] md:text-xs text-red-100 tracking-widest uppercase mt-0.5 truncate">
              One click helps us win{remaining ? ` · ${remaining}` : ""}
            </div>
          </div>
        </div>
        <span className="shrink-0 bg-white text-red-700 hover:bg-yellow-300 hover:text-black font-display tracking-[0.2em] uppercase text-xs md:text-sm px-3 md:px-5 py-2 border-2 border-white transition-colors">
          Vote →
        </span>
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-1 right-1 p-1 text-white/70 hover:text-white"
        aria-label="Dismiss"
        data-testid="chat-vote-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
